import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { motion, AnimatePresence, useInView } from "framer-motion";

// Replace these imports with your actual file paths
import img1 from "../../assets/homeAssets/classic.jpg";
import img2 from "../../assets/homeAssets/softWispy.JPEG";
import img3 from "../../assets/homeAssets/megaVolume.png";
import img4 from "../../assets/homeAssets/megaWispy.jpg";

// --- Data ---
const LASH_STYLES = [
  {
    id: 1,
    title: "Classic Lashes",
    description:
      "Elongated outer corners for a winged, sultry look that mimics eyeliner.",
    image: img1,
  },
  {
    id: 2,
    title: "Soft Wispy Lashes",
    description:
      "Longer lashes in the center of the eye to create a wide-awake, rounded appearance.",
    image: img2,
  },
  {
    id: 3,
    title: "Mega Volume Lashes",
    description:
      "A fluttery, textured mix of lengths for a natural yet spiked Kim-K inspired finish.",
    image: img3,
  },
  {
    id: 4,
    title: "Mega Wispy",
    description:
      "A fluttery, textured mix of lengths for a natural yet spiked Kim-K inspired finish.",
    image: img4,
  },
];

// --- Styled Components ---

const Container = styled.section`
  display: flex;
  flex-direction: column; /* Mobile first */
  min-height: 200vh; /* Enough height to scroll through all items */
  background-color: #fffaf9;

  @media (min-width: 1024px) {
    flex-direction: row;
    min-height: 100vh;
  }
`;

const LeftSide = styled.div`
  width: 100%;
  padding: 0 5%;
  order: 2; /* Text scrolls under the sticky image on mobile */

  @media (min-width: 1024px) {
    width: 50%;
    padding-left: 10%;
    order: 1;
  }
`;

const RightSide = styled.div`
  width: 100%;
  height: 60vh; /* Fixed viewport height for image on mobile */
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  background-color: #fffaf9;
  order: 1;

  @media (min-width: 1024px) {
    width: 50%;
    height: 100vh;
    order: 2;
  }
`;

const TextBlock = styled.div`
  height: 70vh; /* Each text section takes up most of the screen */
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2rem 1rem;

  @media (min-width: 1024px) {
    height: 100vh;
    padding-right: 50px;
  }
`;

const StyleTitle = styled(motion.h2)`
  font-family: "Lovelace", serif;
  font-size: 2.8rem;
  font-style: italic;
  color: #695959;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    font-size: 4rem;
  }

  @media (min-width: 1024px) {
    font-size: 5rem;
  }
`;

const StyleDesc = styled(motion.p)`
  font-family: "Georgia", serif;
  font-size: 1.1rem;
  line-height: 1.7;
  color: #4a3a3d;
  display: none;
  max-width: 450px;

  @media (min-width: 1024px) {
    font-size: 1.2rem;
  }
`;

const ImageFrame = styled(motion.div)`
  width: 85%;
  height: 75%;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.2);
  position: relative;
  transform: skewX(-3deg);

  @media (min-width: 1024px) {
    width: 80%;
    height: 70%;
    border-radius: 40px;
    transform: skewX(-5deg);
  }
`;

const StyledImage = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
`;

// --- Sub-component for individual text triggers ---

const LashTextItem = ({ data, setInView }) => {
  const ref = useRef(null);
  // Detect if we are on mobile to adjust the trigger threshold
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth < 1024);
  }, []);

  const isInView = useInView(ref, {
    amount: isMobile ? 0.4 : 0.6,
    margin: "0px 0px -20% 0px",
  });

  useEffect(() => {
    if (isInView) {
      setInView(data);
    }
  }, [isInView, data, setInView]);

  return (
    <TextBlock ref={ref}>
      <StyleTitle
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        viewport={{ once: false }}
      >
        {data.title}
      </StyleTitle>
      <StyleDesc
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2 }}
        viewport={{ once: false }}
      >
        {data.description}
      </StyleDesc>
    </TextBlock>
  );
};

// --- Main Section Component ---

const LashStyles = () => {
  const [activeStyle, setActiveStyle] = useState(LASH_STYLES[0]);

  return (
    <Container>
      {/* RightSide contains the Image and is STICKY */}
      <RightSide>
        <ImageFrame>
          <AnimatePresence mode="wait">
            <StyledImage
              key={activeStyle.id}
              src={activeStyle.image}
              alt={activeStyle.title}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.6, ease: [0.43, 0.13, 0.23, 0.96] }}
            />
          </AnimatePresence>
        </ImageFrame>
      </RightSide>

      {/* LeftSide contains the scrolling text content */}
      <LeftSide>
        {LASH_STYLES.map((style) => (
          <LashTextItem
            key={style.id}
            data={style}
            setInView={setActiveStyle}
          />
        ))}
        {/* Spacer to allow the final item to trigger before reaching the end of the page */}
        <div style={{ height: "30vh" }} />
      </LeftSide>
    </Container>
  );
};

export default LashStyles;
